import React, { useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import { useEffectAsync } from '../../../../../../helpers';
import useTranslation from '../../../../../../Hooks/useTranslation';
import { SelectValue } from '../../../Select';
import {
	AvailableDoctor,
	useLazyAvailableDoctorsQuery,
} from '../../../../../../graphql/graphql';

export enum WorkplaceUserSelectType {
	DOCTOR = 'doctor',
}

interface Props {
	workplaceId?: string;
	type: WorkplaceUserSelectType;
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	placeholder?: string;
}

const WorkplaceUserSelect: React.FC<Props> = ({
	workplaceId,
	type,
	onChange,
	value,
	placeholder,
}) => {
	const [searchAvailableDoctors] = useLazyAvailableDoctorsQuery();
	const { t } = useTranslation();
	const [innerValue, setInnerValue] = useState<any>();
	const [selectInputValue, setSelectInputValue] = useState<string>('');
	const [options, setOptions] = useState<SelectValue[]>([]);
	const [page, setPage] = useState(1);

	const getFullName = (availableDoctor: AvailableDoctor): string => {
		return [
			availableDoctor.salutationBefore,
			availableDoctor.firstName,
			availableDoctor.lastName,
			availableDoctor.salutationAfter,
		]
			.filter(Boolean)
			.join(' ');
	};

	const handleChange = (val: SelectValue) => {
		onChange?.(val);
	};

	const handleSearchItem = async (
		searchInput: string,
		page?: number,
	): Promise<SelectValue[]> => {
		const { availableDoctors } = await searchAvailableDoctors({
			pattern: searchInput,
			nurse: type !== WorkplaceUserSelectType.DOCTOR,
			workplaceId,
			pagination: {
				page: page || 1,
			},
		}).unwrap();

		const items: SelectValue[] = [];

		for (const availableDoctor of availableDoctors?.data || []) {
			if (availableDoctor) {
				items.push({
					value: availableDoctor.id,
					label: getFullName(availableDoctor),
				});
			}
		}

		return items;
	};

	const handleLoadOptionsBySearch = async (inputValue: string) => {
		setPage(1);
		const newOptions = await handleSearchItem(inputValue, 1);
		setOptions(newOptions);
	};

	const handleLoadMoreOptions = async () => {
		setPage(prev => prev + 1);
		const newOptions = await handleSearchItem(selectInputValue, page + 1);
		setOptions(prevOptions => [...prevOptions, ...newOptions]);
	};

	useEffectAsync(async () => {
		await handleLoadOptionsBySearch(selectInputValue);
	}, [selectInputValue]);

	useEffect(() => {
		setInnerValue(value);
	}, [value]);

	return (
		<ReactSelect
			classNamePrefix='reactSelect'
			className='withSearch'
			options={options}
			value={innerValue}
			onChange={handleChange}
			menuPortalTarget={document.body}
			isSearchable={true}
			onMenuScrollToBottom={handleLoadMoreOptions}
			onInputChange={setSelectInputValue}
			placeholder={t(placeholder ?? 'base.shared.pick')}
			noOptionsMessage={() => t('base.startTyping')}
		/>
	);
};

export default WorkplaceUserSelect;
